import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Break() {
  return (
    <div className="tutorial-container read-only">
      <h1 className="tutorial-title">⛔ JavaScript break Statement</h1>

      {/* Explanation */}
      <div className="tutorial-section">
        <h2 className="section-title">💡 What is break?</h2>
        <p>
          The <code className="highlight_code ">break</code> statement is used
          to <b>exit a loop or switch</b> immediately. It stops further
          iterations even if the loop condition is still true.
        </p>
        <p>
          At <b>Edu-Tech Master Academy</b>,{" "}
          <code className="highlight_code ">break</code> helps control loops
          efficiently, preventing unnecessary iterations.
        </p>
      </div>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{ background: "linear-gradient(135deg, #e0f2fe, #bae6fd)" }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code className="highlight_code ">break</code> immediately exits the
            current loop or switch block.
          </li>
          <li>
            Used inside <code className="highlight_code ">for</code>,{" "}
            <code className="highlight_code ">while</code>, or{" "}
            <code className="highlight_code ">do...while</code> loops.
          </li>
          <li>
            In <code className="highlight_code ">switch</code> statements, it
            prevents fall-through to subsequent cases.
          </li>
          <li>
            Does not terminate outer loops unless used with labels in nested
            loops.
          </li>
        </ul>
      </div>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Stop Loop on Condition</h2>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`for (let i = 1; i <= 5; i++) {
  if (i === 3) {
    break; // Stop loop at 3
  }
  console.log("Count at Edu-Tech Master Academy:", i);
}`}
          />
          <TryItButton
            code={`for (let i = 1; i <= 5; i++) {
  if (i === 3) {
    break;
  }
  console.log("Count at Edu-Tech Master Academy:", i);
}`}
          />
        </div>
        <OutputBox>
          <pre>
            Count at Edu-Tech Master Academy: 1{"\n"}
            Count at Edu-Tech Master Academy: 2
          </pre>
        </OutputBox>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Break in while Loop</h2>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let i = 1;
while (i <= 5) {
  if (i === 4) {
    break;
  }
  console.log("Number:", i);
  i++;
}`}
          />
          <TryItButton
            code={`let i = 1;
while (i <= 5) {
  if (i === 4) {
    break;
  }
  console.log("Number:", i);
  i++;
}`}
          />
        </div>
        <OutputBox>
          <pre>
            Number: 1{"\n"}
            Number: 2{"\n"}
            Number: 3
          </pre>
        </OutputBox>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Break in switch</h2>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`let day = "Tuesday";
switch(day) {
  case "Monday":
    console.log("Start of week");
    break;
  case "Tuesday":
    console.log("Middle of week");
    break;
  default:
    console.log("Another day");
}`}
          />
          <TryItButton
            code={`let day = "Tuesday";
switch(day) {
  case "Monday":
    console.log("Start of week");
    break;
  case "Tuesday":
    console.log("Middle of week");
    break;
  default:
    console.log("Another day");
}`}
          />
        </div>
        <OutputBox>
          <pre>Middle of week</pre>
        </OutputBox>
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Breaking Nested Loops</h2>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`for (let i = 1; i <= 3; i++) {
  for (let j = 1; j <= 3; j++) {
    if (j === 2) break; // Stops inner loop
    console.log(\`i=\${i}, j=\${j}\`);
  }
}`}
          />
          <TryItButton
            code={`for (let i = 1; i <= 3; i++) {
  for (let j = 1; j <= 3; j++) {
    if (j === 2) break;
    console.log(\`i=\${i}, j=\${j}\`);
  }
}`}
          />
        </div>
        <OutputBox>
          <pre>
            i=1, j=1{"\n"}
            i=2, j=1{"\n"}
            i=3, j=1
          </pre>
        </OutputBox>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#1e40af" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Try using <code className="highlight_code ">break</code> in nested
            loops and label statements.
          </li>
          <li>
            Compare <code className="highlight_code ">break</code> with{" "}
            <code className="highlight_code ">continue</code>.
          </li>
          <li>
            Use <code className="highlight_code ">break</code> inside switch
            cases for proper flow control.
          </li>
          <li>
            Experiment with loops to optimize performance and avoid unnecessary
            iterations.
          </li>
        </ul>
      </div>
    </div>
  );
}
